import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tent, AlertCircle, Loader2, X, CheckCircle2, Circle,
  UserPlus, FileText, Clock, Users, Plus, Save, Info, ChevronDown, ArrowRight, ShieldAlert,
  History, RotateCcw, ArrowLeft,
} from 'lucide-react';
import { bootcampService, familyService } from '../services';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';

// ─── Brand colours ────────────────────────────────────────────────────────────
const BRAND = {
  SMARTKIDS:  { hex: '#05F58D', text: '#065F46', label: 'SmartKids',  ages: 'Ages 7–11',  type: 'SMARTKIDS_BOOTCAMP',  parsed: 'SMARTKIDS 2026'  },
  SUPERTEENS: { hex: '#7134EB', text: '#ffffff', label: 'SuperTeens', ages: 'Ages 12–21', type: 'SUPERTEENS_BOOTCAMP', parsed: 'SUPERTEENS 2026' },
};

// ─── camp_type parser ─────────────────────────────────────────────────────────
const parseCampType = (t) => {
  if (t === 'SMARTKIDS_BOOTCAMP')  return 'SMARTKIDS 2026';
  if (t === 'SUPERTEENS_BOOTCAMP') return 'SUPERTEENS 2026';
  return null;
};

const CAMP_TYPE_DESC = {
  'SMARTKIDS 2026':  'Designed for young learners aged 7–11. Builds creativity, teamwork, and essential life skills through hands-on activities and expert coaching.',
  'SUPERTEENS 2026': 'An intensive leadership programme for teens aged 12–21. Unlock full potential through immersive challenges, mentorship, and real-world skill building.',
};

// ─── Error code → human message ───────────────────────────────────────────────
const ASSIGN_ERRORS = {
  ALREADY_ASSIGNED:           'This bootcamp seat has already been assigned.',
  CHILD_NOT_IN_FAMILY:        'This child is not a member of your family.',
  FAMILY_SERVICE_UNAVAILABLE: 'Family service is temporarily unavailable. Please try again.',
  FORBIDDEN:                  'You are not authorized to assign this bootcamp.',
  BOOTCAMP_NOT_FOUND:         'Bootcamp not found.',
  INVALID_PAYLOAD:            'Assignment failed due to a missing field. Please try again.',
};

// ─── Camp Info Modal ──────────────────────────────────────────────────────────
function CampInfoModal({ bootcamp, onClose }) {
  const campTypeLabel = parseCampType(bootcamp.camp_type);
  const typeDesc      = campTypeLabel ? CAMP_TYPE_DESC[campTypeLabel] : null;
  const cfg = STATUS_CFG_PREVIEW[bootcamp.status] || { label: bootcamp.status, color: 'bg-slate-100 text-slate-500' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        data-testid="camp-info-modal"
        initial={{ opacity: 0, scale: 0.95, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 16 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        {/* Colour bar */}
        <div className={`h-1.5 ${campTypeLabel === 'SUPERTEENS 2026' ? 'bg-gradient-to-r from-violet-400 to-purple-500' : 'bg-gradient-to-r from-sky-400 to-blue-500'}`} />

        {/* Header */}
        <div className="px-8 pt-7 pb-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${campTypeLabel === 'SUPERTEENS 2026' ? 'bg-violet-50' : 'bg-sky-50'}`}>
              <Tent size={24} className={campTypeLabel === 'SUPERTEENS 2026' ? 'text-violet-500' : 'text-sky-500'} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-outfit leading-snug">{bootcamp.camp_id}</h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {campTypeLabel && (
                  <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full border
                    ${campTypeLabel === 'SUPERTEENS 2026'
                      ? 'bg-violet-50 text-violet-600 border-violet-100'
                      : 'bg-sky-50 text-sky-600 border-sky-100'}`}>
                    {campTypeLabel}
                  </span>
                )}
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
              </div>
            </div>
          </div>
          <button data-testid="camp-info-close-btn" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-5">
          {typeDesc && (
            <div className={`p-4 rounded-2xl border ${campTypeLabel === 'SUPERTEENS 2026' ? 'bg-violet-50/50 border-violet-100' : 'bg-sky-50/50 border-sky-100'}`}>
              <p className={`text-xs font-bold uppercase tracking-wider mb-2 ${campTypeLabel === 'SUPERTEENS 2026' ? 'text-violet-600' : 'text-sky-600'}`}>Who is this for?</p>
              <p className="text-sm text-slate-700 leading-relaxed">{typeDesc}</p>
            </div>
          )}

          {bootcamp.assigned_child_email && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users size={15} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Assigned To</p>
                <p className="text-sm font-semibold text-slate-800">{bootcamp.assigned_child_email}</p>
              </div>
            </div>
          )}

          {bootcamp.created_at && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock size={15} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Enrolled</p>
                <p className="text-sm font-semibold text-slate-800">
                  {new Date(bootcamp.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          )}

          {bootcamp.assigned_at && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock size={15} className="text-slate-500" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Seat Assigned</p>
                <p className="text-sm font-semibold text-slate-800">
                  {new Date(bootcamp.assigned_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="px-8 pb-7">
          <button onClick={onClose} className="w-full py-3 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Preview-only STATUS_CFG (used before the main STATUS_CFG const is defined)
const STATUS_CFG_PREVIEW = {
  PENDING_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-amber-100 text-amber-700' },
  ACTIVE:             { label: 'Active',             color: 'bg-green-100  text-green-700' },
  COMPLETED:          { label: 'Completed',          color: 'bg-slate-100  text-slate-600' },
};

// ─── Assign Modal ─────────────────────────────────────────────────────────────
function AssignModal({ bootcamp, onClose, onSuccess }) {
  const [children,   setChildren]   = useState([]);
  const [fetching,   setFetching]   = useState(true);
  const [selected,   setSelected]   = useState(null);  // { email, name, isPending }
  const [step,       setStep]       = useState(0);     // 0=select 1,2,3=confirm stacked
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  // Merge actual family children + pending CHILD invites (in case child not yet accepted)
  useEffect(() => {
    Promise.allSettled([
      familyService.getFamily(),
      familyService.listInvitations(),
    ]).then(([famRes, invRes]) => {
      // Keep all children regardless of email; email is needed at assignment time
      const members = famRes.status === 'fulfilled'
        ? (famRes.value?.members || []).filter(m =>
            (m.role || '').toLowerCase() === 'child'
          )
        : [];

      const rawInvites = invRes.status === 'fulfilled'
        ? (Array.isArray(invRes.value) ? invRes.value : (invRes.value?.invitations || []))
        : [];
      const pendingChildInvites = rawInvites.filter(i => i.role === 'CHILD' && i.status === 'PENDING');

      // Dedupe: don't add invite if that email is already a confirmed member
      const memberEmails = new Set(members.map(m => (m.email || '').toLowerCase()));
      const fromInvites = pendingChildInvites
        .filter(i => !memberEmails.has((i.invitee_email || '').toLowerCase()))
        .map(i => ({
          uid:       i.invitation_id,
          name:      i.invitee_name || '',
          email:     i.invitee_email || '',
          role:      'CHILD',
          isPending: true,
        }));

      // Confirmed members first, pending invites second
      setChildren([...members, ...fromInvites]);
    }).catch(() => setError('Failed to load family members. Please try again.'))
      .finally(() => setFetching(false));
  }, []);

  const handleFinalAssign = async () => {
    setSubmitting(true); setError('');
    try {
      await bootcampService.assignBootcamp(bootcamp.enrollment_id, selected.email, selected.uid);
      onSuccess();
    } catch (err) {
      const raw = err?.response?.data?.code
        || err?.response?.data?.detail?.toUpperCase().replace(/ /g, '_')
        || '';
      setError(ASSIGN_ERRORS[raw] || 'Assignment failed. Please try again.');
      setStep(0);
    } finally { setSubmitting(false); }
  };

  // ── Confirm step config ────────────────────────────────────────────────────
  const STEPS = selected ? [
    {
      title: 'Assign Bootcamp Seat?',
      body:  `You're about to assign "${bootcamp.camp_id}" to ${selected.display_name || selected.name || selected.email || 'this child'}.`,
      yes:   'Continue',
    },
    {
      title: 'Confirm Gmail Account',
      body:  'Please verify this is the correct Google account the child will use to sign in. Once assigned, changing it requires admin support.',
      gmail: selected.email,   // shown prominently in this step
      yes:   'Yes, this is correct',
    },
    {
      title: 'Final Confirmation',
      body:  `This will permanently link this bootcamp seat to "${selected.email}". Are you ready?`,
      yes:   'Assign Now',
      final: true,
    },
  ] : [];
  const Z = [60, 70, 80];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      {/* ── Selection view ── */}
      <motion.div
        data-testid="assign-modal"
        initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md"
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-outfit">Assign Bootcamp Seat</h2>
            <p className="text-slate-500 text-sm mt-0.5">{bootcamp.camp_id}</p>
          </div>
          <button data-testid="close-assign-modal" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400">
            <X size={16} />
          </button>
        </div>

        {fetching ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={24} className="animate-spin text-sky-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                Select Child
              </label>
              {children.length > 0 ? (
                <div className="space-y-2">
                  {children.map(m => (
                    <button
                      key={m.uid}
                      type="button"
                      data-testid={`child-option-${m.uid}`}
                      onClick={() => setSelected(m)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-all
                        ${selected?.uid === m.uid
                          ? 'border-sky-400 bg-sky-50'
                          : 'border-slate-200 hover:border-sky-200 bg-slate-50/50'}`}
                    >
                      <div className="w-9 h-9 bg-white rounded-xl border border-slate-200 flex items-center justify-center flex-shrink-0">
                        <Users size={15} className="text-slate-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-800 truncate">{m.display_name || m.name || m.email || 'Unknown'}</p>
                        <p className="text-xs text-slate-400 truncate">{m.email || <span className="italic text-amber-500">No email linked</span>}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {m.isPending && (
                          <span className="text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">
                            PENDING
                          </span>
                        )}
                        {!m.email && !m.isPending && (
                          <span className="text-[10px] font-bold bg-orange-100 text-orange-600 border border-orange-200 rounded-full px-2 py-0.5">
                            NO EMAIL
                          </span>
                        )}
                        {selected?.uid === m.uid && (
                          <CheckCircle2 size={16} className="text-sky-500" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-700 text-sm text-center">
                  No children in your family yet. Add a child via My Family first.
                </div>
              )}
            </div>

            {error && (
              <div data-testid="assign-error" className="p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm flex items-center gap-2">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 py-3 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="assign-submit-btn"
                type="button"
                onClick={() => setStep(1)}
                disabled={!selected || children.length === 0}
                className="flex-1 py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 disabled:bg-amber-200 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <UserPlus size={14} /> Assign Seat
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* ── 3-step stacked confirm modals ── */}
      {STEPS.map((s, idx) => (
        step >= idx + 1 && (
          <motion.div
            key={idx}
            className={`fixed inset-0 z-[${Z[idx]}] flex items-center justify-center p-4 bg-slate-900/25 backdrop-blur-[2px]`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <motion.div
              data-testid={`assign-confirm-step${idx + 1}`}
              className={`bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 ${s.final ? 'border-2 border-amber-200' : ''}`}
              initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
              transition={{ duration: 0.18 }}
            >
              {s.final && (
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <UserPlus size={20} className="text-amber-600" />
                </div>
              )}

              <h3 className={`text-xl font-bold text-slate-900 font-outfit mb-2 ${s.final ? 'text-center' : ''}`}>
                {s.title}
              </h3>
              <p className={`text-slate-500 text-sm mb-4 ${s.final ? 'text-center' : ''}`}>{s.body}</p>

              {/* Gmail step — prominent email display */}
              {s.gmail && (
                <div className="mb-5 p-4 bg-slate-50 border-2 border-sky-200 rounded-2xl text-center">
                  <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold mb-1">Gmail Account</p>
                  <p data-testid="assign-confirm-email" className="text-base font-bold text-sky-700 break-all">
                    {s.gmail}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={idx === 0 ? () => setStep(0) : () => setStep(idx)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  {idx === 0 ? 'Cancel' : 'Go Back'}
                </button>
                <button
                  data-testid={`assign-confirm-btn-${idx + 1}`}
                  onClick={s.final ? handleFinalAssign : () => setStep(idx + 2)}
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white rounded-2xl font-semibold text-sm transition-all"
                >
                  {submitting && s.final ? <Loader2 size={13} className="animate-spin" /> : null}
                  {submitting && s.final ? 'Assigning…' : s.yes}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )
      ))}
    </div>
  );
}

// ─── Field label map for version history display ──────────────────────────────
const FIELD_LABELS = {
  about_my_child: 'About My Child',
  love_letter:    'Secret Letter',
  about_myself:   'About Myself',
};

// ─── Version History Panel ────────────────────────────────────────────────────
function VersionHistoryPanel({ enrollmentId, onRestore, onClose }) {
  const [view,           setView]           = useState('list');
  const [history,        setHistory]        = useState([]);
  const [loadingList,    setLoadingList]    = useState(true);
  const [preview,        setPreview]        = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [restoring,      setRestoring]      = useState(false);
  const [versionError,   setVersionError]   = useState('');

  useEffect(() => {
    bootcampService.getDocumentHistory(enrollmentId)
      .then(data => setHistory(data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingList(false));
  }, [enrollmentId]);

  const handleView = async (version, andShowView = 'preview') => {
    setLoadingVersion(true); setVersionError('');
    try {
      const data = await bootcampService.getDocumentVersion(enrollmentId, version);
      setPreview(data);
      setView(andShowView);
    } catch { setVersionError('Failed to load version. Please try again.'); }
    finally { setLoadingVersion(false); }
  };

  const handleConfirmRestore = async () => {
    setRestoring(true);
    try {
      await onRestore(preview.content);
      onClose();
    } catch { setVersionError('Restore failed. Please try again.'); setView('preview'); }
    finally { setRestoring(false); }
  };

  const fmtDate = iso => {
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); }
    catch { return iso; }
  };

  const backView = view === 'confirm' ? 'preview' : 'list';

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-[2px]"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <motion.div
        data-testid="version-history-panel"
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} transition={{ duration: 0.18 }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {view !== 'list' && (
              <button onClick={() => setView(backView)}
                className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 transition-colors">
                <ArrowLeft size={15} />
              </button>
            )}
            <h3 className="text-lg font-bold text-slate-900 font-outfit">
              {view === 'list'    ? 'Version History'
               : view === 'preview' ? `Version ${preview?.version}`
               : 'Restore this version?'}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[22rem] overflow-y-auto">

          {/* List */}
          {view === 'list' && (
            loadingList ? (
              <div className="flex items-center justify-center py-10 gap-2 text-slate-400">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading history…</span>
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-10 italic">
                No saved versions yet. Click Save Draft to create one.
              </p>
            ) : (
              <div className="space-y-3">
                {history.map((v, i) => (
                  <div key={v.version} className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">Version {v.version}</span>
                          {i === 0 && (
                            <span className="text-[10px] font-bold bg-sky-100 text-sky-600 px-2 py-0.5 rounded-full">LATEST</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{fmtDate(v.created_at)}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          data-testid={`view-version-${v.version}`}
                          onClick={() => handleView(v.version)}
                          disabled={loadingVersion}
                          className="text-xs px-3 py-1.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 font-medium disabled:opacity-50 transition-colors"
                        >
                          {loadingVersion ? <Loader2 size={11} className="animate-spin" /> : 'View'}
                        </button>
                        <button
                          data-testid={`restore-version-${v.version}`}
                          onClick={() => handleView(v.version, 'confirm')}
                          disabled={loadingVersion}
                          className="text-xs px-3 py-1.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 font-medium disabled:opacity-50 transition-colors flex items-center gap-1"
                        >
                          <RotateCcw size={10} /> Restore
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {v.changed_fields.filter(f => !f.endsWith('_done')).map(f => (
                        <span key={f} className="text-[10px] bg-white border border-slate-200 text-slate-500 rounded-full px-2 py-0.5">
                          {FIELD_LABELS[f] || f}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Preview */}
          {view === 'preview' && preview && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">{fmtDate(preview.created_at)}</p>
              {Object.entries(preview.content)
                .filter(([k]) => !k.endsWith('_done'))
                .map(([key, val]) => (
                  <div key={key}>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                      {FIELD_LABELS[key] || key}
                    </p>
                    <p className="text-sm text-slate-800 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 whitespace-pre-wrap leading-relaxed">
                      {val || <span className="italic text-slate-400">Empty</span>}
                    </p>
                    {preview.content[`${key}_done`] && (
                      <p className="text-xs text-green-600 flex items-center gap-1 mt-1.5">
                        <CheckCircle2 size={11} /> Marked as done
                      </p>
                    )}
                  </div>
                ))}
            </div>
          )}

          {/* Confirm */}
          {view === 'confirm' && preview && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                This will <span className="font-semibold text-slate-800">replace your current content</span> with{' '}
                Version {preview.version} ({fmtDate(preview.created_at)}). The restored content will be saved immediately.
              </p>
              {versionError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                  <AlertCircle size={13} /> {versionError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex gap-3">
          {view === 'list' && (
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
              Close
            </button>
          )}
          {view === 'preview' && (
            <>
              <button onClick={() => setView('list')}
                className="flex-1 py-2.5 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Back
              </button>
              <button onClick={() => setView('confirm')}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold transition-colors">
                <RotateCcw size={13} /> Restore this version
              </button>
            </>
          )}
          {view === 'confirm' && (
            <>
              <button onClick={() => setView('preview')} disabled={restoring}
                className="flex-1 py-2.5 rounded-2xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="confirm-restore-btn"
                onClick={handleConfirmRestore} disabled={restoring}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white text-sm font-semibold transition-colors">
                {restoring ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                {restoring ? 'Restoring…' : 'Yes, Restore'}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Parent Document Stepper (About My Child → Secret Letter) ────────────────

function ParentDocStepper({ bootcamp, onClose, onSuccess }) {
  const [step, setStep] = useState(0); // 0 = About My Child, 1 = Secret Letter

  const [aboutChild,     setAboutChild]     = useState(bootcamp.about_my_child  || '');
  const [aboutChildDone, setAboutChildDone] = useState(bootcamp.about_my_child_done || false);

  // Stored as love_letter on backend, shown as "Secret Letter" in UI
  const [secretLetter,     setSecretLetter]     = useState(bootcamp.love_letter  || '');
  const [secretLetterDone, setSecretLetterDone] = useState(bootcamp.love_letter_done || false);

  const [saving,    setSaving]    = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const acRef   = useRef(aboutChild);       useEffect(() => { acRef.current   = aboutChild;       }, [aboutChild]);
  const acdRef  = useRef(aboutChildDone);   useEffect(() => { acdRef.current  = aboutChildDone;   }, [aboutChildDone]);
  const slRef   = useRef(secretLetter);     useEffect(() => { slRef.current   = secretLetter;     }, [secretLetter]);
  const sldRef  = useRef(secretLetterDone); useEffect(() => { sldRef.current  = secretLetterDone; }, [secretLetterDone]);
  const stepRef = useRef(step);             useEffect(() => { stepRef.current = step;             }, [step]);

  const buildPayload = useCallback((forStep) => {
    if (forStep === 0) {
      return {
        about_my_child:      acRef.current,
        about_my_child_done: acdRef.current,
      };
    }
    return {
      love_letter:      slRef.current,
      love_letter_done: sldRef.current,
    };
  }, []);

  const doSave = useCallback(async (forStep, andClose = false) => {
    const payload = buildPayload(forStep ?? stepRef.current);
    setSaving(true); setSaveError('');
    try {
      await bootcampService.submitDocument(bootcamp.enrollment_id, payload);
      setLastSaved(new Date());
      if (andClose) onSuccess();
    } catch { setSaveError('Save failed — check your connection.'); }
    finally { setSaving(false); }
  }, [bootcamp.enrollment_id, buildPayload, onSuccess]);

  const handleNext = async () => {
    await doSave(0);
    setStep(1);
    setSaveError(''); setLastSaved(null);
  };

  const handleBack = async () => {
    await doSave(1);
    setStep(0);
    setSaveError(''); setLastSaved(null);
  };

  const handleClose = () => onClose();
  const handleRestoreVersion = async (content) => {
    if (content.about_my_child      !== undefined) setAboutChild(content.about_my_child);
    if (content.about_my_child_done !== undefined) setAboutChildDone(content.about_my_child_done);
    if (content.love_letter         !== undefined) setSecretLetter(content.love_letter);
    if (content.love_letter_done    !== undefined) setSecretLetterDone(content.love_letter_done);
    await bootcampService.submitDocument(bootcamp.enrollment_id, content);
    setLastSaved(new Date());
  };
  const fmt = (d) => d?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const canAdvance = aboutChildDone;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <p className="text-slate-500 text-sm">{bootcamp.camp_id}</p>
            <div className="flex items-center gap-3">
              {saving ? (
                <span className="flex items-center gap-1.5 text-xs text-sky-500 font-medium">
                  <Loader2 size={12} className="animate-spin" /> Saving…
                </span>
              ) : lastSaved ? (
                <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                  <CheckCircle2 size={12} /> Saved {fmt(lastSaved)}
                </span>
              ) : null}
              <button
                data-testid="version-history-btn"
                onClick={() => setShowHistory(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 text-xs font-semibold transition-colors"
              >
                <Clock size={12} /> History
              </button>
              <button onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Step tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => step === 1 && handleBack()}
              data-testid="step-tab-0"
              className={`flex-1 py-2.5 px-4 rounded-2xl text-sm font-semibold transition-all ${
                step === 0
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                {aboutChildDone && <CheckCircle2 size={13} />}
                About My Child
                {!aboutChildDone && <span className="text-xs opacity-70 font-normal ml-1">— required first</span>}
              </span>
            </button>
            <button
              onClick={() => step === 0 && canAdvance && handleNext()}
              data-testid="step-tab-1"
              disabled={!canAdvance}
              className={`flex-1 py-2.5 px-4 rounded-2xl text-sm font-semibold transition-all ${
                step === 1
                  ? 'bg-slate-800 text-white shadow-sm'
                  : canAdvance
                    ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    : 'bg-slate-50 text-slate-300 cursor-not-allowed'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                {secretLetterDone && <CheckCircle2 size={13} />}
                Secret Letter
                {!canAdvance && <span className="text-xs opacity-60 font-normal ml-1">— unlocks after step 1</span>}
              </span>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {step === 0 ? (
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-outfit mb-1">About My Child</h2>
              <p className="text-slate-400 text-sm mb-5">
                Tell us about your child — their personality, strengths, dreams, and anything you'd like the coaches to know.
                <br /><span className="text-sky-500 font-medium">Mark as Done to unlock the next step.</span>
              </p>
              <textarea
                data-testid="about-child-input"
                value={aboutChild}
                onChange={e => setAboutChild(e.target.value)}
                rows={10}
                placeholder="My child is creative and loves exploring new ideas. They're excited about this camp because…"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all resize-none"
              />
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-outfit mb-1">Secret Letter</h2>
              <p className="text-slate-400 text-sm mb-5">
                Write something heartfelt for your child — to be read at a special moment during camp.
                Only your child will see this.
              </p>
              <textarea
                data-testid="secret-letter-input"
                value={secretLetter}
                onChange={e => setSecretLetter(e.target.value)}
                rows={10}
                placeholder="Dear [child's name]…"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all resize-none"
              />
            </div>
          )}
        </div>

        {saveError && (
          <div className="mx-8 mb-2 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-xs flex items-center gap-2">
            <AlertCircle size={13} /> {saveError}
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center gap-3">
          <button
            data-testid="version-history-btn"
            onClick={() => setShowHistory(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-2xl border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors"
          >
            <History size={12} /> History
          </button>

          {step === 0 ? (
            <button
              data-testid="about-child-done-toggle"
              onClick={() => setAboutChildDone(v => !v)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-xs font-semibold transition-colors ${
                aboutChildDone ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {aboutChildDone ? <CheckCircle2 size={12} /> : <Circle size={12} />}
              {aboutChildDone ? 'Marked Done' : 'Mark as Done'}
            </button>
          ) : (
            <button
              data-testid="secret-letter-done-toggle"
              onClick={() => setSecretLetterDone(v => !v)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-xs font-semibold transition-colors ${
                secretLetterDone ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {secretLetterDone ? <CheckCircle2 size={12} /> : <Circle size={12} />}
              {secretLetterDone ? 'Marked Done' : 'Mark as Done'}
            </button>
          )}

          <button
            data-testid="save-draft-btn"
            onClick={() => doSave(step)}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Draft
          </button>

          {step === 0 ? (
            <button
              data-testid="next-step-btn"
              onClick={handleNext}
              disabled={!canAdvance || saving}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-2xl text-xs font-semibold transition-colors ${
                canAdvance
                  ? 'bg-sky-500 hover:bg-sky-600 text-white'
                  : 'bg-slate-100 text-slate-300 cursor-not-allowed'
              }`}
            >
              Continue → Secret Letter
            </button>
          ) : (
            <button
              data-testid="done-btn"
              onClick={() => doSave(1, true)}
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-900 disabled:bg-slate-400 text-white text-xs font-semibold transition-colors"
            >
              <CheckCircle2 size={12} /> Save & Close
            </button>
          )}
        </div>
      </motion.div>

      {/* Version History */}
      <AnimatePresence>
        {showHistory && (
          <VersionHistoryPanel
            enrollmentId={bootcamp.enrollment_id}
            onRestore={handleRestoreVersion}
            onClose={() => setShowHistory(false)}
          />
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

// ─── Reusable document field ──────────────────────────────────────────────────
function DocField({ label, description, placeholder, value, onChange, done, onToggleDone, testIdPrefix, rows = 6 }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-bold text-slate-800">{label}</label>
        <button
          data-testid={`${testIdPrefix}-done-toggle`}
          type="button" onClick={onToggleDone}
          className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-full transition-colors ${done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
        >
          {done ? <CheckCircle2 size={11} /> : <Circle size={11} />}
          {done ? 'Marked done' : 'Mark done'}
        </button>
      </div>
      <p className="text-slate-400 text-xs mb-2">{description}</p>
      <textarea
        data-testid={`${testIdPrefix}-input`}
        value={value} onChange={e => onChange(e.target.value)}
        rows={rows} placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:bg-white focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition-all resize-none"
      />
    </div>
  );
}

// ─── Child Document Modal ─────────────────────────────────────────────────────
function ChildDocModal({ bootcamp, onClose, onSuccess }) {
  const [aboutMyself,     setAboutMyself]     = useState(bootcamp.about_myself || '');
  const [aboutMyselfDone, setAboutMyselfDone] = useState(bootcamp.about_myself_done || false);
  const [saving,    setSaving]    = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const amRef  = useRef(aboutMyself);      useEffect(() => { amRef.current  = aboutMyself;      }, [aboutMyself]);
  const amdRef = useRef(aboutMyselfDone);  useEffect(() => { amdRef.current = aboutMyselfDone;  }, [aboutMyselfDone]);

  const doSave = useCallback(async (andClose = false) => {
    const payload = {
      about_myself:      amRef.current,
      about_myself_done: amdRef.current,
    };
    setSaving(true); setSaveError('');
    try {
      await bootcampService.submitDocument(bootcamp.enrollment_id, payload);
      setLastSaved(new Date());
      if (andClose) onSuccess();
    } catch { setSaveError('Save failed — check your connection.'); }
    finally { setSaving(false); }
  }, [bootcamp.enrollment_id, onSuccess]);

  const handleClose = () => onClose();
  const handleRestoreVersion = async (content) => {
    if (content.about_myself      !== undefined) setAboutMyself(content.about_myself);
    if (content.about_myself_done !== undefined) setAboutMyselfDone(content.about_myself_done);
    await bootcampService.submitDocument(bootcamp.enrollment_id, content);
    setLastSaved(new Date());
  };
  const fmt = (d) => d?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-900 font-outfit">My Story</h2>
            <p className="text-slate-500 text-sm mt-0.5">{bootcamp.camp_id}</p>
          </div>
          <div className="flex items-center gap-3">
            {saving ? (
              <span className="flex items-center gap-1.5 text-xs text-sky-500 font-medium">
                <Loader2 size={12} className="animate-spin" /> Saving…
              </span>
            ) : lastSaved ? (
              <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium">
                <CheckCircle2 size={12} /> Saved {fmt(lastSaved)}
              </span>
            ) : null}
            <button
              data-testid="version-history-btn"
              onClick={() => setShowHistory(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 text-xs font-semibold transition-colors"
            >
              <Clock size={12} /> History
            </button>
            <button data-testid="close-child-doc-modal" onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-400">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          <DocField
            label="About Myself"
            description="Tell us about yourself — your interests, dreams, and why you're excited for camp."
            placeholder="I love building things and learning new skills. I'm excited because…"
            value={aboutMyself}
            onChange={setAboutMyself}
            done={aboutMyselfDone}
            onToggleDone={() => setAboutMyselfDone(v => !v)}
            testIdPrefix="about-myself"
            rows={12}
          />
        </div>

        {saveError && (
          <div className="mx-8 mb-2 p-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-xs flex items-center gap-2">
            <AlertCircle size={13} /> {saveError}
          </div>
        )}

        <div className="px-8 py-5 border-t border-slate-100 flex items-center gap-3">
          <button
            data-testid="child-version-history-btn"
            onClick={() => setShowHistory(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-2xl border border-slate-200 text-slate-500 text-xs font-semibold hover:bg-slate-50 transition-colors"
          >
            <History size={12} /> History
          </button>
          <button
            data-testid="about-myself-done-toggle"
            onClick={() => setAboutMyselfDone(v => !v)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-2xl text-xs font-semibold transition-colors ${
              aboutMyselfDone ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {aboutMyselfDone ? <CheckCircle2 size={12} /> : <Circle size={12} />}
            {aboutMyselfDone ? 'Marked Done' : 'Mark as Done'}
          </button>
          <button
            data-testid="save-child-draft-btn"
            onClick={() => doSave()}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 transition-colors"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Draft
          </button>
          <button
            data-testid="child-done-btn"
            onClick={() => doSave(true)}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-2xl bg-violet-500 hover:bg-violet-600 disabled:bg-violet-300 text-white text-xs font-semibold transition-colors"
          >
            <CheckCircle2 size={12} /> Save & Close
          </button>
        </div>
      </motion.div>

      {/* Version History */}
      <AnimatePresence>
        {showHistory && (
          <VersionHistoryPanel
            enrollmentId={bootcamp.enrollment_id}
            onRestore={handleRestoreVersion}
            onClose={() => setShowHistory(false)}
          />
        )}
      </AnimatePresence>
    </div>
    </>
  );
}

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CFG = {
  PENDING_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-amber-100 text-amber-700' },
  ACTIVE:             { label: 'Active',             color: 'bg-green-100  text-green-700' },
  COMPLETED:          { label: 'Completed',          color: 'bg-slate-100  text-slate-600' },
};

function DocBadge({ label, done }) {
  return (
    <span className={`flex items-center gap-1 text-xs font-medium ${done ? 'text-green-600' : 'text-slate-400'}`}>
      {done ? <CheckCircle2 size={11} /> : <Circle size={11} />} {label}
    </span>
  );
}

// ─── Bootcamp Card — Parent ───────────────────────────────────────────────────
function ParentCard({ bc, onAssign, onEditDocs, onInfo }) {
  const cfg = STATUS_CFG[bc.status] || { label: bc.status, color: 'bg-slate-100 text-slate-500' };
  const isAssigned    = !!bc.assigned_child_email;
  const campTypeLabel = parseCampType(bc.camp_type);
  const isSuperteens  = bc.camp_type === 'SUPERTEENS_BOOTCAMP';
  const brandHex      = isSuperteens ? BRAND.SUPERTEENS.hex : BRAND.SMARTKIDS.hex;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      data-testid="bootcamp-card"
      onClick={onInfo}
      className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300"
    >
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${brandHex}cc, ${brandHex})` }} />
      <div className="p-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${brandHex}1a` }}>
            <Tent size={22} style={{ color: brandHex }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {campTypeLabel && (
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full border"
                style={{ backgroundColor: `${brandHex}18`, color: isSuperteens ? '#6d28d9' : '#065F46', borderColor: `${brandHex}40` }}>
                {campTypeLabel}
              </span>
            )}
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
          </div>
        </div>

        <h3 className="font-bold text-slate-900 text-lg font-outfit mb-4 leading-snug">{bc.camp_id}</h3>

        <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-2xl">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-white rounded-xl flex items-center justify-center flex-shrink-0 border border-slate-200">
              <Users size={13} className="text-slate-400" />
            </div>
            {isAssigned ? (
              <span className="text-sm font-medium text-slate-700 truncate">{bc.assigned_child_email}</span>
            ) : (
              <span className="text-sm text-slate-400 italic">Child's (Unassigned)</span>
            )}
          </div>
          {!isAssigned && (
            <button
              data-testid="assign-plus-btn"
              onClick={e => { e.stopPropagation(); onAssign(); }}
              className="w-7 h-7 flex items-center justify-center rounded-xl text-white transition-colors flex-shrink-0 ml-2"
              style={{ backgroundColor: brandHex, color: isSuperteens ? '#fff' : '#065F46' }}
              title="Assign to a child"
            >
              <Plus size={14} />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-4 mb-5">
          <DocBadge label="Secret Letter"  done={bc.love_letter_done} />
          <DocBadge label="About My Child" done={bc.about_my_child_done} />
        </div>

        {bc.created_at && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
            <Clock size={11} />
            Enrolled {new Date(bc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        <button
          data-testid="edit-parent-docs-btn"
          onClick={e => { e.stopPropagation(); onEditDocs(); }}
          className="w-full py-3 rounded-2xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
          style={{ backgroundColor: brandHex, color: isSuperteens ? '#fff' : '#065F46' }}
        >
          <FileText size={15} /> Edit My Documents
        </button>
      </div>
    </motion.div>
  );
}

// ─── Bootcamp Card — Child ────────────────────────────────────────────────────
function ChildCard({ bc, onEditDocs, onInfo }) {
  const cfg = STATUS_CFG[bc.status] || { label: bc.status, color: 'bg-slate-100 text-slate-500' };
  const campTypeLabel = parseCampType(bc.camp_type);
  const isSuperteens  = bc.camp_type === 'SUPERTEENS_BOOTCAMP';
  const brandHex      = isSuperteens ? BRAND.SUPERTEENS.hex : BRAND.SMARTKIDS.hex;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      data-testid="bootcamp-card"
      onClick={onInfo}
      className="bg-white rounded-3xl border border-slate-200/60 shadow-sm overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300"
    >
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${brandHex}cc, ${brandHex})` }} />
      <div className="p-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${brandHex}1a` }}>
            <Tent size={22} style={{ color: brandHex }} />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {campTypeLabel && (
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full border"
                style={{ backgroundColor: `${brandHex}18`, color: isSuperteens ? '#6d28d9' : '#065F46', borderColor: `${brandHex}40` }}>
                {campTypeLabel}
              </span>
            )}
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${cfg.color}`}>{cfg.label}</span>
          </div>
        </div>

        <h3 className="font-bold text-slate-900 text-lg font-outfit mb-2 leading-snug">{bc.camp_id}</h3>

        <div className="flex gap-4 mb-4">
          <DocBadge label="About Myself" done={bc.about_myself_done} />
        </div>

        {bc.assigned_at && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
            <Clock size={11} />
            Assigned {new Date(bc.assigned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        )}

        <button
          data-testid="edit-child-docs-btn"
          onClick={e => { e.stopPropagation(); onEditDocs(); }}
          className="w-full py-3 rounded-2xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
          style={{ backgroundColor: brandHex, color: isSuperteens ? '#fff' : '#065F46' }}
        >
          <FileText size={15} /> Write My Story
        </button>
      </div>
    </motion.div>
  );
}

// ─── Camp Filter Dropdown ─────────────────────────────────────────────────────
function CampFilterDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = BRAND[value];
  const other  = value === 'SMARTKIDS' ? BRAND.SUPERTEENS : BRAND.SMARTKIDS;
  const otherKey = value === 'SMARTKIDS' ? 'SUPERTEENS' : 'SMARTKIDS';

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="camp-filter-btn"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 pl-4 pr-3 py-2.5 rounded-2xl font-semibold text-sm transition-all shadow-sm hover:opacity-90 active:scale-95"
        style={{ backgroundColor: active.hex, color: active.text }}
      >
        <span className="w-2 h-2 rounded-full bg-current opacity-70" />
        {active.label}
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.14 }}
            className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden z-30"
          >
            {(['SMARTKIDS', 'SUPERTEENS']).map(key => {
              const b = BRAND[key];
              const isSelected = value === key;
              return (
                <button
                  key={key}
                  data-testid={`filter-${key.toLowerCase()}`}
                  onClick={() => { onChange(key); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm text-left transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: b.hex }} />
                  <span className="font-semibold text-slate-800">{b.label}</span>
                  <span className="text-xs text-slate-400 ml-1">{b.ages}</span>
                  {isSelected && <CheckCircle2 size={13} className="ml-auto flex-shrink-0" style={{ color: b.hex }} />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function MyCamps() {
  const { familyRole, tshirtSize } = useAuth();
  const { setShowAccountSettings } = useUI();

  const [bootcamps,   setBootcamps]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [docModal,    setDocModal]    = useState(null);
  const [infoModal,   setInfoModal]   = useState(null);
  const [campFilter,  setCampFilter]  = useState('SMARTKIDS');

  const isParent  = familyRole === 'parent' || familyRole === 'admin';
  const isChild   = familyRole === 'child';
  // Gate: child must have tshirt_size set before accessing My Camps
  const isGated   = isChild && !tshirtSize;

  const loadBootcamps = useCallback(() => {
    setLoading(true); setError('');
    bootcampService.getBootcamps()
      .then(data => setBootcamps(data.bootcamps || []))
      .catch(() => setError('Failed to load bootcamps. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadBootcamps(); }, [loadBootcamps]);

  // Filter by selected type
  const filteredBootcamps = bootcamps.filter(bc => {
    if (campFilter === 'SUPERTEENS') return bc.camp_type === 'SUPERTEENS_BOOTCAMP';
    // SmartKids is default — includes explicit SMARTKIDS_BOOTCAMP and empty/unknown types
    return bc.camp_type === 'SMARTKIDS_BOOTCAMP' || !bc.camp_type || bc.camp_type === '';
  });

  const activeBrand = BRAND[campFilter];
  const otherKey    = campFilter === 'SMARTKIDS' ? 'SUPERTEENS' : 'SMARTKIDS';
  const otherBrand  = BRAND[otherKey];

  if (isGated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          data-testid="camps-gated"
          className="bg-white rounded-3xl shadow-sm border border-slate-200 p-12 text-center max-w-sm w-full"
        >
          <div className="w-16 h-16 bg-violet-50 rounded-3xl flex items-center justify-center mx-auto mb-5">
            <ShieldAlert size={28} className="text-violet-500" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2 font-outfit">One more step!</h3>
          <p className="text-slate-500 text-sm mb-7 leading-relaxed">
            Please complete your profile — specifically your <span className="font-semibold text-slate-700">T-Shirt Size</span> — to access your camps.
          </p>
          <button
            data-testid="complete-profile-btn"
            onClick={() => setShowAccountSettings(true)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-sm transition-all bg-violet-500 hover:bg-violet-600 active:scale-95 text-white"
          >
            Complete Profile <ArrowRight size={14} />
          </button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

        {/* Page header */}
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2"
              style={{ color: activeBrand.hex }}>
              <span className="w-6 h-px inline-block" style={{ backgroundColor: activeBrand.hex }} /> My Camps
            </p>
            <h1 className="text-4xl font-bold text-slate-900 font-outfit">My Bootcamps</h1>
            <p className="text-slate-500 mt-1">
              {isParent
                ? 'Manage your bootcamp seats, assign children, and write your parent letters.'
                : 'Your assigned bootcamps — complete your self-introduction letter.'}
            </p>
          </div>

          {/* Filter dropdown */}
          <CampFilterDropdown value={campFilter} onChange={setCampFilter} />
        </div>

        {error && (
          <div data-testid="camps-error" className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-center gap-3 text-red-700 text-sm">
            <AlertCircle size={16} /> {error}
            <button onClick={loadBootcamps} className="ml-auto text-xs font-semibold underline">Retry</button>
          </div>
        )}

        {/* ── Grid or empty states ── */}
        {filteredBootcamps.length > 0 ? (
          <div data-testid="camps-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBootcamps.map(bc =>
              isParent
                ? <ParentCard key={bc.enrollment_id} bc={bc}
                    onAssign={() => setAssignModal(bc)}
                    onEditDocs={() => setDocModal(bc)}
                    onInfo={() => setInfoModal(bc)} />
                : <ChildCard key={bc.enrollment_id} bc={bc}
                    onEditDocs={() => setDocModal(bc)}
                    onInfo={() => setInfoModal(bc)} />
            )}
          </div>
        ) : bootcamps.length > 0 ? (
          /* Has camps but none match the current filter — suggest switching */
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-12 text-center max-w-sm w-full">
              <div className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-5"
                style={{ backgroundColor: `${otherBrand.hex}18` }}>
                <Tent size={28} style={{ color: otherBrand.hex }} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Nothing here yet!
              </h3>
              <p className="text-slate-500 text-sm mb-7 leading-relaxed">
                No <span className="font-semibold">{activeBrand.label}</span> bootcamps found.
                Do you have a <span className="font-semibold">{otherBrand.label}</span> enrolment instead?
              </p>
              <button
                data-testid="switch-filter-btn"
                onClick={() => setCampFilter(otherKey)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-sm transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: otherBrand.hex, color: otherBrand.text }}
              >
                Try {otherBrand.label} Bootcamp
                <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        ) : (
          /* No camps at all */
          <div className="bg-white rounded-3xl border border-slate-200/60 shadow-sm p-12 text-center">
            <div className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${activeBrand.hex}18` }}>
              <Tent size={28} style={{ color: activeBrand.hex }} />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">No bootcamps yet</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              {isParent
                ? 'Your bootcamp enrolments will appear here after a purchase is confirmed.'
                : 'Your parent will assign a bootcamp seat to you once enrolled.'}
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {infoModal && (
          <CampInfoModal
            key="camp-info"
            bootcamp={infoModal}
            onClose={() => setInfoModal(null)}
          />
        )}
        {assignModal && (
          <AssignModal
            key="assign"
            bootcamp={assignModal}
            onClose={() => setAssignModal(null)}
            onSuccess={() => { setAssignModal(null); loadBootcamps(); }}
          />
        )}
        {docModal && isParent && (
          <ParentDocStepper
            key="parent-docs"
            bootcamp={docModal}
            onClose={() => setDocModal(null)}
            onSuccess={() => { setDocModal(null); loadBootcamps(); }}
          />
        )}
        {docModal && !isParent && (
          <ChildDocModal
            key="child-docs"
            bootcamp={docModal}
            onClose={() => setDocModal(null)}
            onSuccess={() => { setDocModal(null); loadBootcamps(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
